import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Archive, ChevronDown, ChevronUp, Printer, RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';
import { useAuth } from '../../contexts/AuthContext';
import { API } from '../../utils/kitchenTasks';
import { formatDateFR, escapeHtml } from '../../utils/dateFormat';
import { formatApiError } from '../../services/api';

const STATUS = { draft: ['Brouillon', 'bg-amber-50 text-amber-700 border-amber-200'], finalized: ['Terminée', 'bg-emerald-50 text-emerald-700 border-emerald-200'], a_valider: ['À valider', 'bg-violet-50 text-violet-700 border-violet-200'], validee: ['Validée', 'bg-emerald-50 text-emerald-700 border-emerald-200'], en_reception: ['En réception', 'bg-cyan-50 text-cyan-700 border-cyan-200'], receptionnee: ['Réceptionnée', 'bg-emerald-50 text-emerald-800 border-emerald-300'], commandee: ['Commandée', 'bg-emerald-50 text-emerald-800 border-emerald-300'], annulee: ['Annulée', 'bg-gray-100 text-gray-500 border-gray-200'] };

// Lecture seule : commandes directes du chef + commandes consolidées (permission view_orders)
const OrdersReadOnly = () => {
  const { hasPermission } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const direct = (await axios.get(`${API}/orders/`)).data.data || [];
      let consolidated = [];
      if (hasPermission('view_all_order_requests')) consolidated = (await axios.get(`${API}/order-requests/orders/consolidated`)).data.data || [];
      const all = [...direct.map(o => ({ ...o, kind: 'directe' })), ...consolidated.map(o => ({ ...o, kind: 'consolidée' }))];
      all.sort((a, b) => (b.date || '').localeCompare(a.date || '') || (b.created_at || '').localeCompare(a.created_at || ''));
      setOrders(all);
    } catch (err) { toast.error(formatApiError(err)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const print = (o) => {
    const e = escapeHtml;
    const rows = (o.items || []).filter(i => i.quantity > 0).map(i => `<tr><td>${e(i.product_name)}</td><td>${e(i.category || '')}</td><td style="text-align:right">${e(i.quantity)} ${e(i.unit || '')}</td></tr>`).join('');
    const w = window.open('', '_blank');
    w.document.write(`<html><head><title>Commande ${e(o.date)}</title><style>body{font-family:Arial;padding:24px}table{width:100%;border-collapse:collapse}td,th{border-bottom:1px solid #ddd;padding:6px;text-align:left}</style></head><body><h2>Commande du ${e(formatDateFR(o.date))}</h2>${o.delivery_date ? `<p>Livraison souhaitée : ${e(formatDateFR(o.delivery_date))}</p>` : ''}<table><thead><tr><th>Produit</th><th>Catégorie</th><th style="text-align:right">Quantité</th></tr></thead><tbody>${rows}</tbody></table></body></html>`);
    w.document.close(); w.print();
  };

  return (
    <div className="max-w-6xl mx-auto" data-testid="orders-readonly">
      <div className="flex items-center justify-between gap-3 mb-4">
        <p className="text-gray-500 text-sm">Commandes enregistrées par le chef et la direction — consultation uniquement.</p>
        <Button variant="outline" onClick={load} className="h-11 rounded-full border-gray-200" data-testid="orders-readonly-refresh-btn"><RefreshCw size={16} className={loading ? 'animate-spin' : ''} /></Button>
      </div>
      {loading ? <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 text-neon-pink animate-spin" /></div>
        : orders.length === 0 ? <div className="bg-white border border-dashed border-gray-300 rounded-2xl p-8 text-center text-gray-600" data-testid="orders-readonly-empty"><Archive className="mx-auto mb-2 text-gray-400" />Aucune commande enregistrée.</div>
        : <div className="space-y-3">
          {orders.map(o => {
            const items = (o.items || []).filter(i => (i.quantity || 0) > 0);
            const [label, cls] = STATUS[o.status] || [o.status, 'bg-gray-100 text-gray-600 border-gray-200'];
            const isOpen = open === o.id;
            return (
              <div key={o.id} className="bg-white border border-gray-200 rounded-2xl overflow-hidden" data-testid={`ro-order-${o.id}`}>
                <button type="button" onClick={() => setOpen(isOpen ? null : o.id)} className="w-full flex flex-wrap items-center gap-2 px-4 py-3 text-left min-h-[56px]" data-testid={`ro-order-toggle-${o.id}`}>
                  <span className="font-heading font-bold text-gray-900">{formatDateFR(o.date)}</span>
                  <span className={`text-xs px-2.5 py-1 rounded-full border ${cls}`}>{label}</span>
                  <span className="text-xs px-2.5 py-1 rounded-full bg-gray-100 text-gray-600 border border-gray-200">{o.kind}</span>
                  <span className="text-sm text-gray-500 ml-auto">{items.length} produit{items.length > 1 ? 's' : ''}{o.created_by_name ? ` · ${o.created_by_name}` : ''}</span>
                  {isOpen ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
                </button>
                {isOpen && (
                  <div className="border-t border-gray-100 px-4 py-3">
                    {o.delivery_date && <p className="text-xs text-gray-500 mb-2">Livraison souhaitée : {formatDateFR(o.delivery_date)}</p>}
                    <table className="w-full text-sm">
                      <tbody>{items.map((i, idx) => (
                        <tr key={`${i.product_id || i.product_name}-${idx}`} className="border-b border-gray-100 last:border-0">
                          <td className="py-1.5 text-gray-900">{i.product_name}</td>
                          <td className="py-1.5 text-xs text-gray-500">{i.category || ''}</td>
                          <td className="py-1.5 text-right font-medium text-gray-800">{i.quantity} {i.unit || ''}</td>
                        </tr>
                      ))}</tbody>
                    </table>
                    {hasPermission('print_order') && <Button variant="outline" size="sm" onClick={() => print(o)} className="mt-3 rounded-full border-gray-200 text-gray-700" data-testid={`ro-order-print-${o.id}`}><Printer size={14} className="mr-1" />Imprimer</Button>}
                  </div>
                )}
              </div>
            );
          })}
        </div>}
    </div>
  );
};

export default OrdersReadOnly;
